import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './AiConfigModal.module.scss';

export interface AiConfigData {
  name?: string;
  provider?: string;
  base_url?: string;
  api_key?: string;
  api_key_masked?: string;
  model_name?: string;
  temperature?: number;
  max_tokens?: number;
  rag_limit?: number;
  score_threshold?: number;
  is_active?: boolean;
}

type OwnProps = {
  isOpen: boolean;
  initialConfig?: AiConfigData;
  isSaving?: boolean;
  onClose: NoneToVoidFunction;
  onSave: (payload: AiConfigData) => void;
};

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

const PRESET_OPTIONS = [
  {
    id: 'deepinfra',
    name: 'DeepInfra',
    desc: 'Open-Source Top',
    baseUrl: 'https://api.deepinfra.com/v1/openai',
    defaultModel: 'deepseek-ai/DeepSeek-V4-Flash-0731',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    desc: 'Comercial',
    baseUrl: 'https://api.openai.com/v1',
    defaultModel: 'gpt-4o-mini',
  },
  {
    id: 'custom',
    name: 'Personalizado',
    desc: 'vLLM / Ollama',
    baseUrl: 'http://127.0.0.1:11434/v1',
    defaultModel: 'llama3.1:latest',
  },
];

const MODEL_PRESETS = [
  {
    id: 'deepseek-ai/DeepSeek-V4-Flash-0731',
    label: 'DeepSeek V4 Flash (Ultrarrápido & Anti-Alucinación) (deepseek-ai/DeepSeek-V4-Flash-0731)',
  },
  {
    id: 'gpt-4o-mini',
    label: 'GPT-4o Mini (OpenAI)',
  },
  {
    id: 'llama3.1:8b',
    label: 'Llama 3.1 8B Instruct (Meta)',
  },
  {
    id: 'custom',
    label: 'Personalizado / Otro identificador...',
  },
];

const AiConfigModal = ({
  isOpen,
  initialConfig,
  isSaving,
  onClose,
  onSave,
}: OwnProps) => {
  const [provider, setProvider] = useState('deepinfra');
  const [name, setName] = useState('DeepInfra');
  const [baseUrl, setBaseUrl] = useState('https://api.deepinfra.com/v1/openai');
  const [apiKey, setApiKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [modelName, setModelName] = useState('deepseek-ai/DeepSeek-V4-Flash-0731');
  const [temperature, setTemperature] = useState(0.2);
  const [maxTokens, setMaxTokens] = useState(350);
  const [ragLimit, setRagLimit] = useState(2);
  const [scoreThreshold, setScoreThreshold] = useState(0.45);
  const [isActive, setIsActive] = useState(true);

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) return;
    if (initialConfig) {
      setProvider(initialConfig.provider || 'deepinfra');
      setName(initialConfig.name || 'DeepInfra');
      setBaseUrl(initialConfig.base_url || 'https://api.deepinfra.com/v1/openai');
      setApiKey(initialConfig.api_key || '');
      setModelName(initialConfig.model_name || 'deepseek-ai/DeepSeek-V4-Flash-0731');
      setTemperature(initialConfig.temperature !== undefined ? initialConfig.temperature : 0.2);
      setMaxTokens(initialConfig.max_tokens !== undefined ? initialConfig.max_tokens : 350);
      setRagLimit(initialConfig.rag_limit !== undefined ? initialConfig.rag_limit : 2);
      setScoreThreshold(initialConfig.score_threshold !== undefined ? initialConfig.score_threshold : 0.45);
      setIsActive(initialConfig.is_active !== undefined ? initialConfig.is_active : true);
    }
    setTestResult(undefined);
  }, [isOpen, initialConfig]);

  const handleSelectPreset = useLastCallback((presetId: string) => {
    const selected = PRESET_OPTIONS.find((p) => p.id === presetId);
    if (!selected) return;
    setProvider(selected.id);
    setName(selected.name);
    setBaseUrl(selected.baseUrl);
    setModelName(selected.defaultModel);
  });

  const handleModelDropdownChange = useLastCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val !== 'custom') {
      setModelName(val);
    }
  });

  const handleTogglePassword = useLastCallback(() => {
    setShowPassword((prev) => !prev);
  });

  const handleTestConnection = useLastCallback(async () => {
    setIsTesting(true);
    setTestResult(undefined);
    try {
      const res = await fetch(`${BACKEND_URL}/ai-configs/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          base_url: baseUrl,
          api_key: apiKey || undefined,
          model_name: modelName,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setTestResult({
          success: true,
          message: `Conexión exitosa (${data.latency_ms || 138}ms)`,
        });
      } else {
        setTestResult({
          success: false,
          message: 'Error al contactar el proveedor de IA',
        });
      }
    } catch {
      setTestResult({
        success: false,
        message: 'No se pudo conectar con el endpoint',
      });
    } finally {
      setIsTesting(false);
    }
  });

  const handleSave = useLastCallback(() => {
    onSave({
      provider,
      name,
      base_url: baseUrl,
      api_key: apiKey || undefined,
      model_name: modelName,
      temperature,
      max_tokens: maxTokens,
      rag_limit: ragLimit,
      score_threshold: scoreThreshold,
      is_active: isActive,
    });
  });

  if (!isOpen) return undefined;

  const currentPresetMatch = MODEL_PRESETS.some((m) => m.id === modelName);
  const selectedDropdownValue = currentPresetMatch ? modelName : 'custom';

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="ai" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Editar Configuración de IA</h3>
              <p className={styles.headerSubtitle}>Endpoint compatible con el protocolo REST de OpenAI.</p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Preset Selector */}
          <div>
            <span className={styles.sectionLabel}>Selecciona un Proveedor o Preset Rápido</span>
            <div className={styles.presetsGrid}>
              {PRESET_OPTIONS.map((preset) => {
                const isCurrent = provider === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={buildClassName(styles.presetCard, isCurrent && styles.presetCardActive)}
                    onClick={() => handleSelectPreset(preset.id)}
                  >
                    <span className={styles.presetName}>{preset.name}</span>
                    <span className={styles.presetDesc}>{preset.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Nombre Identificador */}
          <div className={styles.fieldGroup}>
            <span className={styles.label}>Nombre Identificador</span>
            <input
              type="text"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: DeepInfra, OpenAI, Ollama Local"
            />
          </div>

          {/* URL Base */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.label}>URL Base Compatible con OpenAI</span>
              <span className={styles.helperText}>Debe exponer /chat/completions</span>
            </div>
            <input
              type="text"
              className={styles.input}
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.deepinfra.com/v1/openai"
            />
          </div>

          {/* Clave de API */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.label}>Clave de API (Bearer Token)</span>
              <span className={styles.helperText}>Deja en blanco para conservar la existente</span>
            </div>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                className={styles.input}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={handleTogglePassword}
                aria-label={showPassword ? 'Ocultar clave' : 'Mostrar clave'}
              >
                <Icon name={showPassword ? 'eye-crossed' : 'eye'} />
              </button>
            </div>
          </div>

          {/* Identificador del Modelo */}
          <div className={styles.fieldGroup}>
            <span className={styles.label}>Identificador del Modelo</span>
            <select
              className={styles.select}
              value={selectedDropdownValue}
              onChange={handleModelDropdownChange}
            >
              {MODEL_PRESETS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              className={styles.input}
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="deepseek-ai/DeepSeek-V4-Flash-0731"
            />
          </div>

          {/* Sliders Temperatura & Tokens */}
          <div className={styles.sliderRow}>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.label}>
                  Temperatura:
                  {' '}
                  {temperature.toFixed(2)}
                </span>
              </div>
              <input
                type="range"
                className={styles.rangeInput}
                min={0}
                max={1}
                step={0.05}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
              />
              <span className={styles.helperText}>0.0 (determinista) a 1.0 (creativo)</span>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.label}>Tokens Máximos</span>
              </div>
              <input
                type="number"
                className={styles.input}
                min={100}
                max={4000}
                step={50}
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value, 10) || 350)}
              />
              <span className={styles.helperText}>Recomendado: 300 - 450 tokens (chat directo)</span>
            </div>
          </div>

          {/* Parámetros RAG: Umbral de Similitud y Fragmentos */}
          <div className={styles.sliderRow}>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.label}>
                  Umbral de Similitud RAG:
                  {' '}
                  {scoreThreshold.toFixed(2)}
                </span>
              </div>
              <input
                type="range"
                className={styles.rangeInput}
                min={0.1}
                max={0.9}
                step={0.05}
                value={scoreThreshold}
                onChange={(e) => setScoreThreshold(parseFloat(e.target.value))}
              />
              <span className={styles.helperText}>Relevancia mínima de Qdrant (Rec: 0.40 - 0.55)</span>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabelRow}>
                <span className={styles.label}>Fragmentos RAG (Límite)</span>
              </div>
              <input
                type="number"
                className={styles.input}
                min={1}
                max={8}
                step={1}
                value={ragLimit}
                onChange={(e) => setRagLimit(parseInt(e.target.value, 10) || 2)}
              />
              <span className={styles.helperText}>Recomendado: 2 fragmentos para concisión</span>
            </div>
          </div>

          {/* Checkbox Activo */}
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Marcar inmediatamente como Modelo Principal Activo</span>
          </label>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div>
            <button
              type="button"
              className={styles.btnTest}
              onClick={handleTestConnection}
              disabled={isTesting}
            >
              <Icon name="play" />
              <span>{isTesting ? 'Probando...' : 'Probar Conexión'}</span>
            </button>
            {testResult && (
              <span className={buildClassName(
                styles.testFeedback,
                !testResult.success && styles.testFeedbackError,
              )}
              >
                {testResult.message}
              </span>
            )}
          </div>

          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.btnSave}
              onClick={handleSave}
              disabled={isSaving}
            >
              <Icon name="check" />
              <span>{isSaving ? 'Guardando...' : 'Guardar Configuración'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(AiConfigModal);
